import { useState } from 'react';
import { X, ChatCircle as MessageCircle, PaperPlaneTilt as Send, Envelope as Mail, Copy, Check } from '@phosphor-icons/react';
import { useShowcaseText } from '../i18n/useShowcaseText';
import './ShareModal.css';

/**
 * Desktop share modal. Mobile uses native `navigator.share` instead, so
 * this component isn't rendered there.
 *
 * Contents:
 *   - Preview block: image + title + price — gives the user a "this is
 *     what messengers will show" reassurance before they share.
 *   - Read-only URL field + Copy button (with "Copied!" feedback for 2s).
 *   - Direct-share shortcuts to WhatsApp / Telegram / Email via deep
 *     links — saves a copy-paste step for the most common channels.
 *
 * The URL the user actually shares is the b2b-api link-preview endpoint
 * (#909, MR !808) — messenger crawlers parse its OG metadata for the
 * preview card.
 */
const ShareModal = ({
  isOpen,
  onClose,
  title,
  imageUrl,
  previewUrl,
  price,
  year,
  mileage,
  tagline,
}) => {
  const showcaseText = useShowcaseText();
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(previewUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('[ShareModal] Clipboard write failed:', err);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  // Direct-share deep links. `text` carries title + URL for WhatsApp /
  // Email (where URL alone wouldn't show context); Telegram has a
  // dedicated `url` param so we pass title separately.
  const shareText = `${title} — ${previewUrl}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(
    previewUrl
  )}&text=${encodeURIComponent(title)}`;
  const emailUrl = `mailto:?subject=${encodeURIComponent(
    title
  )}&body=${encodeURIComponent(shareText)}`;

  return (
    <div
      className="share-modal__backdrop"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        className="share-modal__card"
        role="dialog"
        aria-modal="true"
        aria-label={showcaseText('showcase.car.share', 'Share this car')}
      >
        <div className="share-modal__header">
          <h2 className="share-modal__title">{showcaseText('showcase.car.share', 'Share this car')}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={showcaseText('showcase.share.close', 'Close share dialog')}
            className="share-modal__close"
          >
            <X className="share-modal__close-icon" />
          </button>
        </div>

        {/* Preview block — что увидит мессенджер при шаринге URL.
         *
         * Layout:
         *   [фото] Title (bold)
         *          2024 · 45,000 km · €15,990  (subtle meta line)
         *          Dealer tagline               (smaller, muted)
         */}
        <div className="share-modal__preview">
          {imageUrl && (
            <img
              src={imageUrl}
              alt=""
              className="share-modal__preview-image"
              loading="lazy"
            />
          )}
          <div className="share-modal__preview-meta">
            <div className="share-modal__preview-title">{title}</div>
            {(() => {
              const bits = [];
              if (typeof year === 'number' && year > 0) bits.push(String(year));
              if (typeof mileage === 'number' && mileage > 0) {
                bits.push(`${mileage.toLocaleString('en-US')} km`);
              }
              if (typeof price === 'number' && price > 0) {
                bits.push(`€${price.toLocaleString('en-US')}`);
              }
              if (bits.length === 0) return null;
              return <div className="share-modal__preview-meta-line">{bits.join(' · ')}</div>;
            })()}
            {tagline && <div className="share-modal__preview-tagline">{tagline}</div>}
          </div>
        </div>

        {/* URL + Copy */}
        <div className="share-modal__url-row">
          <input
            type="text"
            value={previewUrl}
            readOnly
            className="share-modal__url-input"
            onFocus={(e) => e.target.select()}
            aria-label={showcaseText('showcase.share.url', 'Share URL')}
          />
          <button
            type="button"
            onClick={handleCopy}
            className={`share-modal__copy-button ${
              copied ? 'share-modal__copy-button--copied' : ''
            }`}
            aria-label={
              copied
                ? showcaseText('showcase.share.linkCopied', 'Link copied')
                : showcaseText('showcase.share.copyLink', 'Copy link')
            }
          >
            {copied ? (
              <Check className="share-modal__copy-icon" />
            ) : (
              <Copy className="share-modal__copy-icon" />
            )}
            <span>
              {copied ? showcaseText('showcase.share.copied', 'Copied!') : showcaseText('showcase.share.copy', 'Copy')}
            </span>
          </button>
        </div>

        {/* Direct-share shortcuts */}
        <div className="share-modal__direct-label">
          {showcaseText('showcase.share.via', 'Or share via:')}
        </div>
        <div className="share-modal__direct-row">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="share-modal__direct-button"
          >
            <MessageCircle className="share-modal__direct-icon" />
            <span>WhatsApp</span>
          </a>
          <a
            href={telegramUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="share-modal__direct-button"
          >
            <Send className="share-modal__direct-icon" />
            <span>Telegram</span>
          </a>
          <a href={emailUrl} className="share-modal__direct-button">
            <Mail className="share-modal__direct-icon" />
            <span>Email</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
